import { render } from 'preact';
import { effect } from '@preact/signals';
import { App } from './App';
import { initMarkdown } from './core/utils/markdown';
import { connect, forceReconnect, setupVisibilityHandler } from './core/ws/client';
import { isAuthenticated } from './core/auth/store';
import { hydrateFromIdb, startTurnReconciler, subscribeToChatWS } from './modules/chat/chat-store';
import { takeShare } from './modules/chat/share-in';
import { scheduleReloadOnIdle } from './core/sw/update';
import { startWakeLockWatcher } from './core/system/wake-lock';
import { initPush } from './core/push/push';
import { startDebugStreamsOverlay } from './core/debug/streams-overlay';

// Body text uses the system font stack (see --font in global.css); only the
// mono face for tabular numbers is self-hosted.
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';

import './global.css';

const CHUNK_RELOAD_KEY = 'jarvis_chunk_reload_at';
const CHUNK_RELOAD_WINDOW_MS = 30_000;
const SERVICE_WORKER_UPDATE_INTERVAL_MS = 5 * 60_000;

function dynamicImportFailureMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  if (reason && typeof reason === 'object' && 'message' in reason) {
    return String((reason as { message?: unknown }).message ?? '');
  }
  return '';
}

function isDynamicImportFailure(reason: unknown): boolean {
  const message = dynamicImportFailureMessage(reason);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Expected a JavaScript module script|Strict MIME type checking/i.test(message);
}

function reloadOnceForChunkFailure(): void {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0');
  if (Number.isFinite(last) && now - last < CHUNK_RELOAD_WINDOW_MS) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  window.location.reload();
}

window.addEventListener('vite:preloadError', ((event: Event) => {
  event.preventDefault();
  reloadOnceForChunkFailure();
}) as EventListener);

window.addEventListener('unhandledrejection', (event) => {
  if (!isDynamicImportFailure(event.reason)) return;
  event.preventDefault();
  reloadOnceForChunkFailure();
});

window.addEventListener('error', (event) => {
  if (!isDynamicImportFailure(event.error || event.message)) return;
  event.preventDefault();
  reloadOnceForChunkFailure();
});

initMarkdown();

// Something shared into the Jarvis app (PH-07w): its chat, text and files are set before the chat draws (the
// synchronous part runs now); nothing is sent.
void takeShare();

render(<App />, document.getElementById('app')!);

// Realtime bootstrap converges on the authenticated signal so BOTH the boot
// path (page load with an existing token) and the first-login path (token set
// after boot via login()) open the WS. Previously connect() only ran inside a
// boot-time `if (isAuthenticated.value)` block, so a fresh login never opened
// the socket — chat streaming, live approvals and SMS sync stayed dead until a
// manual reload (pwa-console-v2-2). One-time setup (IDB hydrate, visibility +
// wake-lock handlers) is guarded so it runs exactly once; connect() is safe to
// re-run (it closes any stale socket) and picks up the current token, so a
// re-login after a 401 reconnects with the fresh credential (pwa-console-v2-8).
let realtimeBootstrapped = false;
let stopTurnReconciler: (() => void) | null = null;
const HYDRATION_BOOT_BUDGET_MS = 1500;

async function bootstrapRealtime(): Promise<void> {
  subscribeToChatWS();
  stopTurnReconciler?.();
  stopTurnReconciler = startTurnReconciler();
  setupVisibilityHandler();
  startWakeLockWatcher();
  initPush();

  let hydrationFinished = false;
  const hydration = hydrateFromIdb()
    .catch((err) => console.warn('[main] IDB hydration failed:', err))
    .finally(() => { hydrationFinished = true; });
  await Promise.race([
    hydration,
    new Promise<void>((resolve) => setTimeout(resolve, HYDRATION_BOOT_BUDGET_MS)),
  ]);

  connect();
  if (!hydrationFinished) {
    void hydration.then(() => {
      // A late snapshot load happened after the first server truth event.
      // Reconnect once so every restored stream is reconciled authoritatively.
      forceReconnect();
    });
  }
}

effect(() => {
  if (!isAuthenticated.value) {
    stopTurnReconciler?.();
    stopTurnReconciler = null;
    return;
  }

  if (!realtimeBootstrapped) {
    realtimeBootstrapped = true;
    void bootstrapRealtime();
  } else {
    // Re-authenticated after a logout/401 — reconnect with the fresh token.
    if (!stopTurnReconciler) stopTurnReconciler = startTurnReconciler();
    forceReconnect();
  }
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
    .then((registration) => {
      void registration.update().catch((err) => {
        console.warn('[sw] Update check failed:', err);
      });
      window.setInterval(() => {
        void registration.update().catch((err) => {
          console.warn('[sw] Periodic update check failed:', err);
        });
      }, SERVICE_WORKER_UPDATE_INTERVAL_MS);
    })
    .catch((err) => {
      console.error('[sw] Registration failed:', err);
    });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    scheduleReloadOnIdle();
  });
}

startDebugStreamsOverlay();
